// ALRT: price alerts, kept in this browser and checked while the page is open.

import { quotes } from "../api";
import { clear, h } from "../dom";
import * as f from "../fmt";
import { every } from "../panel";
import { register, type Ctx } from "../registry";
import { display, resolve } from "../securities";
import { DataTable } from "../table";

const STORE = "berg.alerts";

export interface Alert {
  id: number;
  sym: string;
  op: ">" | "<";
  price: number;
  created: number;
  fired?: number;
  firedAt?: number;
}

export function loadAlerts(): Alert[] {
  try {
    const a = JSON.parse(localStorage.getItem(STORE) ?? "[]");
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

function saveAlerts(a: Alert[]): void {
  localStorage.setItem(STORE, JSON.stringify(a));
  window.dispatchEvent(new Event("berg-alerts"));
}

/// Parse `AAPL > 250` / `AAPL < 180.5` (tokens after ALRT).
function parseAlert(toks: string[]): Omit<Alert, "id" | "created"> | string {
  const m = /^(.+?)\s*([<>])\s*([0-9.,]+)$/.exec(toks.join(" "));
  if (!m) return "Usage: ALRT AAPL > 250";
  const sym = resolve(m[1].trim().split(/\s+/));
  const price = Number(m[3].replace(/,/g, ""));
  if (!sym || !isFinite(price) || price <= 0) return "Usage: ALRT AAPL > 250";
  return { sym, op: m[2] as ">" | "<", price };
}

function runALRT(ctx: Ctx): void {
  const msg = h("div", { class: "pad" });
  const table = new DataTable<Alert>(
    [
      { label: "Security", get: (a) => display(a.sym), cls: () => "amber" },
      { label: "Condition", get: (a) => `${a.op} ${f.price(a.price)}` },
      { label: "Status", get: (a) => (a.fired ? `fired ${f.stamp(a.fired)} at ${f.price(a.firedAt)}` : "watching"), cls: (_v, a) => (a.fired ? "yellow" : "dim") },
      { label: "Created", get: (a) => a.created, fmt: (v) => f.stamp(v), cls: () => "dim" },
      {
        label: "",
        get: () => 0,
        sort: false,
        fmt: (_v, a) => h("button", { title: "Delete", onclick: () => (saveAlerts(loadAlerts().filter((x) => x.id !== a.id)), render()) }, "×"),
      },
    ],
    { panel: ctx.panel, action: (a) => `${a.sym} GP` },
  );
  const render = () => table.set(loadAlerts());
  if (ctx.args.length) {
    const p = parseAlert(ctx.args);
    if (typeof p === "string") clear(msg, h("span", { class: "down" }, p));
    else {
      saveAlerts([...loadAlerts(), { ...p, id: Date.now(), created: Date.now() }]);
      clear(msg, `Alert set: ${display(p.sym)} ${p.op} ${f.price(p.price)}`);
    }
  }
  const perm =
    "Notification" in window && Notification.permission === "default"
      ? h("button", { onclick: () => Notification.requestPermission().then(() => perm?.remove()) }, "Enable desktop notifications")
      : null;
  clear(
    ctx.body,
    h(
      "div",
      { class: "pad" },
      h("span", { class: "dim" }, "ALRT AAPL > 250 <GO> adds an alert. Alerts live in this browser and are checked every 30s while BERG is open. "),
      perm,
    ),
    msg,
    table.el,
  );
  render();
  window.addEventListener("berg-alerts", render, { signal: ctx.signal });
}

/// Background checker, started once from main.ts. `notify` shows the
/// in-page banner.
export function startAlertWatcher(notify: (text: string) => void): void {
  const check = async () => {
    const live = loadAlerts().filter((a) => !a.fired);
    if (!live.length) return;
    const r = await quotes([...new Set(live.map((a) => a.sym))]);
    const px = new Map(r.data.map((q) => [q.symbol, q.regularMarketPrice]));
    let changed = false;
    const all = loadAlerts();
    for (const a of all) {
      const p = px.get(a.sym);
      if (a.fired || p === undefined) continue;
      if ((a.op === ">" && p > a.price) || (a.op === "<" && p < a.price)) {
        a.fired = Date.now();
        a.firedAt = p;
        changed = true;
        const text = `ALERT ${display(a.sym)} ${f.price(p)} ${a.op} ${f.price(a.price)}`;
        notify(text);
        if ("Notification" in window && Notification.permission === "granted") new Notification("BERG", { body: text });
      }
    }
    if (changed) saveAlerts(all);
  };
  check().catch(() => {});
  every(30_000, check, new AbortController().signal);
}

register(
  {
    code: "ALRT",
    name: "Price Alerts",
    group: "Tools",
    needsSec: false,
    args: /^.+$/,
    help: "Browser-local price alerts. ALRT AAPL > 250.",
    aliases: ["ALERT"],
    run: runALRT,
  },
  {
    code: "IB",
    name: "Instant Bloomberg",
    group: "Tools",
    needsSec: false,
    help: "Chat.",
    run: (ctx) =>
      clear(
        ctx.body,
        h(
          "div",
          { class: "pad prose" },
          h("span", { class: "amber" }, "IB is not available.\n\n"),
          "Messaging, and the fact that everyone you trade with is reachable on it, is the other 50% of a Bloomberg terminal.\n",
          "BERG has the screens. It does not have your counterparties.",
        ),
      ),
  },
);
